import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { BRAND } from "../lib/brandConfig";
import { getAttribution } from "../lib/attribution";
import { track } from "../lib/analytics";
import { CheckCircle2, Loader2 } from "lucide-react";

/**
 * Lead Capture Form — Urban Nest Quest brand.
 * Posts to POST /api/leads which fires the centralized admin notification
 * to ADMIN_NOTIFICATION_EMAIL (internal inbox).
 *
 * Props:
 *   source           - lead source label (e.g., "apartment_search", "luxury", "homes_for_rent")
 *   submitLabel      - button text (default "View Apartments")
 *   onSuccess(lead)  - optional callback after successful submission
 *   compact          - if true, renders as 2-col grid suitable for sticky modals
 */
const LeadCaptureForm = ({ source, submitLabel = "View Apartments", onSuccess, compact = false }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    move_in_date: "",
    budget_range: "",
    preferred_area: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) {
      setStatus("error");
      setErrorMsg("Please provide your name and email.");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");

    // Parse "$2000-$2800" into min/max
    let budget_min, budget_max;
    if (form.budget_range) {
      const nums = (form.budget_range.match(/\d[\d,]*/g) || []).map((n) => parseInt(n.replace(/,/g, ""), 10));
      if (nums.length >= 2) { budget_min = nums[0]; budget_max = nums[1]; }
      else if (nums.length === 1) { budget_max = nums[0]; }
    }

    const attribution = getAttribution();
    const payload = {
      name: `${form.first_name} ${form.last_name}`.trim() || form.first_name,
      email: form.email.trim().toLowerCase(),
      phone: form.phone || null,
      lead_type: "renter",
      source,
      move_in_date: form.move_in_date || null,
      preferred_areas: form.preferred_area ? [form.preferred_area] : [],
      budget_min,
      budget_max,
      message: form.budget_range ? `Budget range: ${form.budget_range}` : null,
      ...attribution,
    };

    try {
      const { data } = await axios.post(`${API}/leads`, payload);
      track("lead_submitted", { source, has_phone: !!form.phone });
      setStatus("success");
      if (onSuccess) onSuccess(data);
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or text us at (936) 224-1484."
      );
    }
  };

  if (status === "success") {
    return (
      <div
        className="rounded-2xl p-6 sm:p-8 text-center bg-gradient-to-br from-[#1F2A44] to-[#2a3556] text-white"
        data-testid="lead-form-success"
      >
        <CheckCircle2 className="w-12 h-12 text-[#C8A96B] mx-auto mb-3" />
        <h3 className="text-xl font-bold mb-2 font-['Playfair_Display']">You're in</h3>
        <p className="text-sm text-white/80">
          Browse the listings below. A locator will reach out shortly with the best matches and any current move-in specials.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full px-4 py-3 rounded-lg border border-[#D9D9D9] bg-white text-[#333333] " +
    "placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#C8A96B] focus:ring-2 focus:ring-[#C8A96B]/20 transition-all";

  const gridCls = compact ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : "grid grid-cols-1 md:grid-cols-2 gap-4";

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl shadow-xl border border-[#D9D9D9] p-6 sm:p-8"
      data-testid="lead-capture-form"
    >
      {!compact && (
        <div className="mb-5">
          <h3 className="text-xl sm:text-2xl font-bold text-[#1F2A44] font-['Playfair_Display']">
            Tell us what you're looking for
          </h3>
          <p className="text-sm text-[#333333]/70 mt-1">
            Get personalized recommendations from your Houston apartment locator.
          </p>
        </div>
      )}

      <div className={gridCls}>
        <input className={inputCls} placeholder="First Name *" value={form.first_name}
          onChange={(e) => setField("first_name", e.target.value)}
          data-testid="lead-input-firstname" required />
        <input className={inputCls} placeholder="Last Name" value={form.last_name}
          onChange={(e) => setField("last_name", e.target.value)}
          data-testid="lead-input-lastname" />
        <input className={inputCls} type="email" placeholder="Email *" value={form.email}
          onChange={(e) => setField("email", e.target.value)}
          data-testid="lead-input-email" required />
        <input className={inputCls} type="tel" placeholder="Phone Number" value={form.phone}
          onChange={(e) => setField("phone", e.target.value)}
          data-testid="lead-input-phone" />
        <input className={inputCls} type="date" placeholder="Desired Move Date" value={form.move_in_date}
          onChange={(e) => setField("move_in_date", e.target.value)}
          data-testid="lead-input-movedate" />
        <select className={inputCls} value={form.budget_range}
          onChange={(e) => setField("budget_range", e.target.value)}
          data-testid="lead-input-budget">
          <option value="">Budget Range</option>
          <option value="$1000-$1500">$1,000 – $1,500</option>
          <option value="$1500-$2000">$1,500 – $2,000</option>
          <option value="$2000-$2800">$2,000 – $2,800</option>
          <option value="$2800-$4000">$2,800 – $4,000</option>
          <option value="$4000-$6000">$4,000 – $6,000</option>
          <option value="$6000-$15000">$6,000+</option>
        </select>
        <input className={`${inputCls} ${compact ? "" : "md:col-span-2"}`}
          placeholder="Preferred Area (e.g., Heights, Energy Corridor, Downtown)"
          value={form.preferred_area}
          onChange={(e) => setField("preferred_area", e.target.value)}
          data-testid="lead-input-area" />
      </div>

      {errorMsg && (
        <p className="mt-3 text-sm text-red-600" data-testid="lead-form-error">{errorMsg}</p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#C8A96B] hover:bg-[#B5915A] text-white text-base font-bold transition-colors shadow-sm disabled:opacity-60"
        data-testid="lead-form-submit"
      >
        {status === "submitting" ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
        ) : submitLabel}
      </button>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-[#333333]/70">
        <span className="inline-flex items-center gap-1" data-testid="trust-no-spam">
          <span className="text-emerald-600 font-bold">✓</span> No Spam
        </span>
        <span className="inline-flex items-center gap-1" data-testid="trust-unsubscribe">
          <span className="text-emerald-600 font-bold">✓</span> Unsubscribe Anytime
        </span>
        <span className="inline-flex items-center gap-1" data-testid="trust-never-sold">
          <span className="text-emerald-600 font-bold">✓</span> Your Info Is Never Sold
        </span>
      </div>
      <p className="mt-2 text-[11px] text-[#333333]/60 text-center">
        100% free service. Prefer to text?{" "}
        <a href={BRAND.smsHref} className="text-[#1F2A44] underline hover:text-[#C8A96B]">
          {BRAND.phone}
        </a>
      </p>
    </form>
  );
};

export default LeadCaptureForm;
